import React, { useState } from 'react';
import {
  Body1,
  Body2,
  Caption1,
  Button,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  LightbulbFilament24Regular,
  Edit20Regular,
  Delete20Regular,
} from '@fluentui/react-icons';
import { PromptSuggestion } from '../types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalL,
    backgroundColor: '#ffffff',
    borderTop: '1px solid #e0e0e0',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalS,
  },
  headerIcon: {
    color: '#0078d4',
  },
  promptsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  promptCard: {
    cursor: 'pointer',
    padding: tokens.spacingHorizontalM,
    transition: 'all 0.2s ease',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    backgroundColor: '#ffffff',
    minHeight: '90px',
    display: 'flex',
    flexDirection: 'column',
    ':hover': {
      backgroundColor: '#f5f5f5',
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0, 120, 212, 0.15)',
      border: '1px solid #0078d4',
    },
  },
  promptHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalXS,
  },
  promptTitle: {
    fontWeight: tokens.fontWeightSemibold,
    color: '#0a1b45',
    flex: 1,
    fontSize: tokens.fontSizeBase300,
  },
  promptText: {
    color: '#616161',
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase300,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    flex: 1,
  },
  categoryBadge: {
    fontSize: '11px',
    fontWeight: '500',
    padding: '2px 8px',
    borderRadius: '4px',
    backgroundColor: '#0078d4',
    color: '#ffffff',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  customBadge: {
    fontSize: '11px',
    fontWeight: '600',
    padding: '2px 8px',
    borderRadius: '4px',
    backgroundColor: '#107C10',
    color: '#ffffff',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  badgesContainer: {
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
  },
  promptCardWithActions: {
    position: 'relative',
  },
  actionButtons: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    display: 'flex',
    gap: '4px',
    opacity: 0,
    transition: 'opacity 0.2s ease',
  },
  actionButtonsVisible: {
    opacity: 1,
  },
  actionButton: {
    minWidth: 'auto',
    padding: '4px',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    border: '1px solid #e0e0e0',
    ':hover': {
      backgroundColor: '#ffffff',
    },
  },
  editButton: {
    ':hover': {
      backgroundColor: '#f0f0f0',
      color: '#0078d4',
    },
  },
  deleteButton: {
    ':hover': {
      backgroundColor: '#fff0f0',
      color: '#d13438',
    },
  },
});

interface PromptListProps {
  prompts: PromptSuggestion[];
  onSelectPrompt: (prompt: PromptSuggestion) => void;
  areaName: string;
  areaColor?: string;
  onEditPrompt?: (prompt: PromptSuggestion) => void;
  onDeletePrompt?: (prompt: PromptSuggestion) => void;
}

export const PromptList: React.FC<PromptListProps> = ({
  prompts,
  onSelectPrompt,
  areaName,
  areaColor,
  onEditPrompt,
  onDeletePrompt,
}) => {
  const styles = useStyles();
  const [hoveredPromptId, setHoveredPromptId] = useState<string | null>(null);

  const handleCardClick = (prompt: PromptSuggestion, event: React.MouseEvent) => {
    // Don't trigger if clicking on action buttons
    const target = event.target as HTMLElement;
    if (target.closest('button')) {
      return;
    }
    console.log('Card clicked:', prompt.text);
    onSelectPrompt(prompt);
  };

  const handleEdit = (prompt: PromptSuggestion, event: React.MouseEvent) => {
    event.stopPropagation();
    if (onEditPrompt) {
      onEditPrompt(prompt);
    }
  };

  const handleDelete = (prompt: PromptSuggestion, event: React.MouseEvent) => {
    event.stopPropagation();
    if (onDeletePrompt) {
      onDeletePrompt(prompt);
    }
  };

  if (prompts.length === 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.promptsGrid}>
        {prompts.map((prompt) => (
          <div
            key={prompt.id}
            title={prompt.text}
            onClick={(e) => handleCardClick(prompt, e)}
            onMouseEnter={() => setHoveredPromptId(prompt.id)}
            onMouseLeave={() => setHoveredPromptId(null)}
            className={`${styles.promptCard} ${styles.promptCardWithActions}`}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleCardClick(prompt, e as any);
              }
            }}
          >
            {/* Action buttons for custom prompts */}
            {prompt.isCustom && (
              <div
                className={`${styles.actionButtons} ${
                  hoveredPromptId === prompt.id ? styles.actionButtonsVisible : ''
                }`}
              >
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<Edit20Regular />}
                  onClick={(e) => handleEdit(prompt, e)}
                  className={`${styles.actionButton} ${styles.editButton}`}
                  title="Edit prompt"
                />
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<Delete20Regular />}
                  onClick={(e) => handleDelete(prompt, e)}
                  className={`${styles.actionButton} ${styles.deleteButton}`}
                  title="Delete prompt"
                />
              </div>
            )}

            <div className={styles.promptHeader}>
              <Body2 className={styles.promptTitle}>
                {prompt.title || prompt.text.substring(0, 30) + '...'}
              </Body2>
              <div className={styles.badgesContainer}>
                {prompt.isCustom && (
                  <span className={styles.customBadge}>
                    Custom
                  </span>
                )}
                {prompt.category && (
                  <span className={styles.categoryBadge}>
                    {prompt.category}
                  </span>
                )}
              </div>
            </div>
            <Caption1 className={styles.promptText}>
              {prompt.text}
            </Caption1>
          </div>
        ))}
      </div>
    </div>
  );
};
